'use client';

import { useEffect } from 'react';
import {
    SHOP_HANDOFF_DIRECTION,
    SHOP_HOSTNAME,
} from '../../lib/external-links';
import {
    getBrowserAttributionIdentity,
} from '../../lib/ad-attribution';

const INTERNAL_CAMPAIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_id',
    'utm_term',
    'utm_content',
];

export function buildAttributedShopHref(href, identity = {}, placement) {
    let url;
    try {
        url = new URL(href, 'https://waterdoctor.ca');
    } catch {
        return href;
    }
    if (url.hostname !== SHOP_HOSTNAME) return href;

    INTERNAL_CAMPAIGN_PARAMS.forEach((key) => url.searchParams.delete(key));
    url.searchParams.set('wd_handoff', SHOP_HANDOFF_DIRECTION);
    if (placement) url.searchParams.set('wd_placement', placement);
    if (identity.journeyId) url.searchParams.set('wd_journey_id', identity.journeyId);
    if (identity.leadId) url.searchParams.set('wd_lead_id', identity.leadId);
    return url.toString();
}

function applyShopAttribution(anchor, identity) {
    if (!anchor || !(anchor instanceof HTMLAnchorElement)) return;
    if (
        anchor.dataset.noShopHandoff === 'true' ||
        anchor.dataset.noShopUtm === 'true'
    ) return;

    const placement =
        new URL(anchor.href, window.location.origin).searchParams.get('wd_placement') ||
        anchor.dataset.wdPlacement ||
        anchor.dataset.utmContent ||
        anchor.dataset.utmTerm;
    const nextHref = buildAttributedShopHref(anchor.href, identity, placement);
    if (anchor.href !== nextHref) {
        anchor.href = nextHref;
    }
}

export function shouldTrackOutboundInteraction(event) {
    return event?.type === 'click' || (event?.type === 'auxclick' && event?.button === 1);
}

export function ShopOutboundAttribution() {
    useEffect(() => {
        const handleInteraction = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            const anchor = target.closest('a[href*="shop.waterdoctor.ca"]');
            if (anchor) {
                const currentIdentity = getBrowserAttributionIdentity();
                applyShopAttribution(anchor, currentIdentity);

                if (!shouldTrackOutboundInteraction(event)) return;
                const url = new URL(anchor.href, window.location.origin);
                const eventData = {
                    destination_path: url.pathname,
                    placement: url.searchParams.get('wd_placement') || 'unspecified',
                    has_journey_id: Boolean(currentIdentity.journeyId),
                    has_lead_id: Boolean(currentIdentity.leadId),
                };
                window.gtag?.('event', 'shop_outbound_click', eventData);
                document.dispatchEvent(
                    new CustomEvent('waterdoctor:shop-outbound-click', {
                        detail: eventData,
                    }),
                );
            }
        };

        const eventNames = ['pointerdown', 'focusin', 'contextmenu', 'click', 'auxclick'];
        eventNames.forEach((eventName) => document.addEventListener(eventName, handleInteraction, true));

        return () => {
            eventNames.forEach((eventName) => document.removeEventListener(eventName, handleInteraction, true));
        };
    }, []);

    return null;
}
